import {
  ChatAgentContext,
  ChatMessage,
  ReadBlockVariant,
  ReadPage,
  StreamEvent,
  UpdateBlockVariant,
} from '@tinct/core-api-models';
import { ApiClient } from '../api-client/api-client';
import {
  ApiCollectionResponse,
  FieldQuery,
  GetCollectionQueryParams,
  GetQueryByIdParams,
} from '../types';
import { ApiError } from '../api-client/api-error';

export interface GetPageChatMessagesStaticFilter {
  pageId: string;
}

export class PagesEndpoint {
  constructor(private client: ApiClient) {}

  async getPageById(params: GetQueryByIdParams<ReadPage>): Promise<ReadPage> {
    return this.client.getById<ReadPage>(
      `/api/v1/pages/${params.id}`,
      params.fields,
    );
  }

  /**
   * Paginated history of the page's single conversation (newest first);
   * empty before the first turn. User messages come back verbatim — a turn
   * sent with a `context` additionally carries a `context` content block
   * (the resolved selection, rendered as a chip). Older conversations may
   * still contain the legacy rewritten text with no context block.
   */
  async getPageChatAgentMessages(
    params: GetCollectionQueryParams<ChatMessage, GetPageChatMessagesStaticFilter>,
  ): Promise<ApiCollectionResponse<ChatMessage>> {
    const { staticParams, fields, offset = 0, limit = 20, filters } = params;

    return this.client.getCollection<ChatMessage>(
      `/api/v1/pages/${staticParams?.pageId}/chat-agent/messages`,
      fields,
      {
        offset,
        limit,
        ...filters,
      },
    );
  }

  async patchBlockVariant(
    pageId: string,
    blockVariantId: string,
    data: Partial<UpdateBlockVariant>,
    fields?: FieldQuery<ReadBlockVariant>,
  ): Promise<ReadBlockVariant> {
    return this.client.patch<ReadBlockVariant>(
      `/api/v1/pages/${pageId}/block-variants/${blockVariantId}`,
      data,
      fields,
    );
  }

  async regeneratePage(
    pageId: string,
    prompt?: string,
  ): Promise<void> {
    return this.client.post<void>(
      `/api/v1/pages/${pageId}/_regenerate`,
      prompt ? { prompt } : undefined,
    );
  }

  async excludePage(pageId: string): Promise<ReadPage> {
    return this.client.post<ReadPage>(`/api/v1/pages/${pageId}/_exclude`, {});
  }

  async includePage(pageId: string): Promise<ReadPage> {
    return this.client.post<ReadPage>(`/api/v1/pages/${pageId}/_include`, {});
  }

  async unpublishPage(pageId: string): Promise<ReadPage> {
    return this.client.post<ReadPage>(`/api/v1/pages/${pageId}/_unpublish`, {});
  }

  /**
   * Puts ONE variant live on its own (no campaign publish job): accepted for a
   * ready or unpublished page, or a published one needing republication.
   */
  async publishPage(pageId: string): Promise<ReadPage> {
    return this.client.post<ReadPage>(`/api/v1/pages/${pageId}/_publish`, {});
  }

  /**
   * The page-level edit chat (SSE). The agent can edit any block of the
   * page; `context.blockId` is the selected core block's id — the backend
   * resolves it to that block's variant on this page and composes the turn,
   * so the client must not bake it into the message text. A stale/foreign
   * block id degrades to a global ask server-side. Each applied edit emits
   * one `data_update` per edited block.
   */
  async postPageMessageChat(
    pageId: string,
    message: string,
    context?: ChatAgentContext | null,
    onEvent?: (event: StreamEvent) => void,
    onError?: (error: ApiError) => void,
    onComplete?: () => void
  ): Promise<void> {
    return this.client.postStream(
      `/api/v1/pages/${pageId}/chat-agent`,
      {
        message,
        // JSON.stringify drops undefined — omitted entirely for a global ask.
        context: context ?? undefined,
      },
      onEvent,
      onError,
      onComplete
    );
  }
}
